/**
 * Phase 5 PR 5.2 — `useMacroPlan` hook.
 *
 * Fetches the user's season macro plan via `GET /api/macro-plan?seasonId=...`
 * (handler at `api/index.js:4803 handleGetMacroPlan`). Endpoint returns:
 *
 *     {
 *       plan:   season_macro_plan rows[]   (sorted by week_number ASC),
 *       status: 'none' | 'proposed' | 'approved' | 'stale'
 *     }
 *
 * Returns the canonical `{ data, loading, error, refetch }` shape plus
 * a derived `status` field at the top level for ergonomic discovery /
 * proposed / approved branching at the route.
 *
 * Uses `useAuthFetch` so the request carries the user's auth token AND
 * the `X-Surface: react` telemetry header (Phase 4a contract).
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from './useAuthFetch';

export type MacroPlanStatus = 'none' | 'proposed' | 'approved' | 'stale';

export interface MacroPlanWeek {
  id: string;
  season_id: string;
  user_id: string;
  week_number: number;
  /** ISO 'YYYY-MM-DD'. NULLable for legacy rows — treat as missing. */
  week_start_date: string | null;
  /** Free-form mode id; resolve via `lib/trainingModes.ts`. */
  mode: string;
  focus: string | null;
  meet_id: string | null;
  is_ai_proposed: boolean | null;
  approved: boolean | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string | null;
}

export interface UseMacroPlanArgs {
  /** Active season id (typically from `useActiveSeason`). When null,
   *  the hook stays idle and returns null. */
  seasonId: string | null;
}

export interface UseMacroPlanResult {
  data: MacroPlanWeek[] | null;
  status: MacroPlanStatus;
  loading: boolean;
  error: Error | null;
  /**
   * B120 — resolves to the STATUS the fetch just landed (the same union
   * `status` above uses), not merely `void`. `routes/plan.tsx onGenerate`
   * reads this return value directly: the state this callback set is only
   * visible on the NEXT render, so a caller that read `status` from its own
   * closure right after `await refetch()` was reading whatever `status` was
   * captured at render time, before the refetch — stale by exactly one
   * fetch. Every caller that ignores the return value keeps compiling.
   */
  refetch: () => Promise<MacroPlanStatus>;
}

const EMPTY_RESULT: UseMacroPlanResult = {
  data: null,
  status: 'none',
  loading: false,
  error: null,
  refetch: async () => 'none',
};

export function useMacroPlan({ seasonId }: UseMacroPlanArgs): UseMacroPlanResult {
  const fetchAuthed = useAuthFetch();
  const { user, supabase } = useAuthContext();

  const [data, setData] = useState<MacroPlanWeek[] | null>(null);
  const [status, setStatus] = useState<MacroPlanStatus>('none');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async (): Promise<MacroPlanStatus> => {
    if (!supabase || !user || !seasonId) {
      setData(null);
      setStatus('none');
      setLoading(false);
      setError(null);
      return 'none';
    }
    setLoading(true);
    setError(null);
    try {
      const url = `/api/macro-plan?seasonId=${encodeURIComponent(seasonId)}`;
      const res = await fetchAuthed(url);
      if (!res.ok) {
        throw new Error(`/api/macro-plan responded ${res.status}`);
      }
      const json = (await res.json()) as { plan?: MacroPlanWeek[]; status?: MacroPlanStatus };
      const plan = Array.isArray(json.plan) ? json.plan : [];
      const nextStatus: MacroPlanStatus =
        json.status === 'proposed' ||
        json.status === 'approved' ||
        json.status === 'stale'
          ? json.status
          : 'none';
      // Sort defensively even though the endpoint already orders by
      // week_number — guards against future API changes.
      plan.sort((a, b) => (a.week_number ?? 0) - (b.week_number ?? 0));
      const resolvedStatus: MacroPlanStatus = plan.length > 0 ? nextStatus : 'none';
      setData(plan.length > 0 ? plan : null);
      setStatus(resolvedStatus);
      return resolvedStatus;
    } catch (err) {
      setData(null);
      setStatus('none');
      setError(err instanceof Error ? err : new Error(String(err)));
      return 'none';
    } finally {
      setLoading(false);
    }
  }, [fetchAuthed, supabase, user, seasonId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  if (!supabase || !user) return EMPTY_RESULT;

  return { data, status, loading, error, refetch };
}
